import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Sprout } from "lucide-react";
import { Profile } from "@/hooks/useProfile";
import { VerificationBadge } from "./VerificationBadge";
import { formatLocationForDisplay } from "@/utils/locationUtils";
import { useLiteMode } from "@/hooks/useLiteMode";

interface ProfileHeaderProps {
  profile: Profile;
  userCropPlansCount: number;
  isVerified?: boolean;
}

export const ProfileHeader = ({ profile, userCropPlansCount, isVerified }: ProfileHeaderProps) => {
  const { isLiteMode } = useLiteMode();
  const profileIcon = profile.farm_images && Array.isArray(profile.farm_images) 
    ? profile.farm_images.find((img: any) => img.role === 'profile_icon' || img.is_logo)
    : null;

  const mainFarmImage = profile.farm_images && Array.isArray(profile.farm_images)
    ? profile.farm_images.find((img: any) => img.role === 'main_farm')
    : null;

  const avatarUrl = profileIcon?.url || profile.avatar_url;
  const mainImageUrl = mainFarmImage?.url || profile.farm_logo_url;

  return (
    <div className="w-full mb-6">
      {/* Main Profile Card - matches the public profile design */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {/* Cover Image */}
        {!isLiteMode && mainImageUrl && (
          <div className="h-32 bg-gradient-to-r from-green-400 to-blue-500 relative">
            <img
              src={mainImageUrl}
              alt="Farm cover"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        {/* Profile Content */}
        <div className="p-4">
          <div className="flex items-start space-x-3">
            {/* Avatar */}
            <div className={`rounded-full border-2 border-green-200 overflow-hidden flex-shrink-0 ${
              isLiteMode ? 'w-10 h-10' : 'w-12 h-12'
            }`}>
              {avatarUrl ? (
                <img 
                  src={avatarUrl} 
                  alt={`${profile.farm_name || profile.username}'s avatar`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-green-100 flex items-center justify-center">
                  <Sprout size={isLiteMode ? 16 : 20} className="text-green-600" />
                </div>
              )}
            </div>
            
            {/* Main Info Section */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h3 className="font-semibold text-gray-800 truncate">
                  {profile.farm_name || profile.username || 'Farm Profile'}
                </h3>
                <VerificationBadge isVerified={isVerified} size="sm" />
              </div>
              <p className="text-gray-500 text-sm">@{profile.username}</p>
              
              {profile.location && (
                <div className="flex items-center space-x-1 text-green-600 text-sm mt-1">
                  <MapPin size={14} />
                  <span className="truncate">{formatLocationForDisplay(profile.location, "Location available")}</span>
                </div>
              )}
              
              {/* Bio */}
              {profile.bio && (
                <p className="text-gray-600 text-sm mt-2 mb-2">{profile.bio}</p>
              )}

              {/* Farm Goals */}
              {profile.farm_goals && profile.farm_goals.length > 0 && (
                <div className="mb-2">
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">My Goals</h4>
                  <div className="flex flex-wrap gap-1">
                    {profile.farm_goals.map((goal, index) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full"
                      >
                        {goal}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Vertical Stats on the Right */}
            <div className="flex flex-col gap-2 flex-shrink-0">
              <div className="p-2 bg-green-50 rounded-lg text-center min-w-[70px]">
                <div className="text-lg font-bold text-green-600">{userCropPlansCount}</div>
                <div className="text-xs text-gray-600">Crops</div>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg text-center min-w-[70px]">
                <div className="text-sm font-bold text-blue-600">
                  {profile.farm_size || 'N/A'}
                </div>
                <div className="text-xs text-gray-600">Size</div>
              </div>
              <div className="p-2 bg-orange-50 rounded-lg text-center min-w-[70px]">
                <div className="text-sm font-bold text-orange-600">
                  {profile.usda_zone ? profile.usda_zone.replace('Zone ', '') : 'N/A'}
                </div>
                <div className="text-xs text-gray-600">Zone</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
